<template>
  <div class="slip-totals relative text-gray-700">
    <div :class="{ 'blur-sm select-none': locked }" :aria-hidden="locked ? 'true' : null">
      <!-- set like the match card's home / draw / away: a small grey label over an 18px figure -->
      <div class="flex w-full justify-center gap-1 text-center tabular-nums" :class="figureSize">
        <div class="flex-1 min-w-0 text-xs text-gray-500">
          <button type="button" class="term" :aria-expanded="String(explain === 'fair')" v-on:click="toggle('fair')">
            fair odds
          </button>
          <span class="figure">{{ locked ? "?.?" : format(totals && totals.fair_odds) }}</span>
        </div>
        <div class="flex-1 min-w-0 text-xs text-gray-500">
          <button type="button" class="term" :aria-expanded="String(explain === 'best')" v-on:click="toggle('best')">
            best odds
          </button>
          <span class="figure">{{ format(totals && totals.odds) }}</span>
        </div>
        <div class="flex-1 min-w-0 text-xs text-gray-500">
          value
          <span class="figure" :style="locked ? null : { color: edgeColour }">{{ locked ? "??" : edgeText }}</span>
        </div>
      </div>
      <!-- tap a term for what it means -->
      <div v-if="explain" class="mt-3 rounded-lg bg-white border border-gray-200 shadow-sm px-3 py-2 text-xs text-gray-700 flex gap-2" role="note">
        <p class="flex-1">{{ explanations[explain] }}</p>
        <button type="button" class="flex-none -m-1 p-1 text-gray-400" aria-label="Close" v-on:click="explain = null">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
            stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <!-- one line each, 12px apart, like every other line in the slip's grey footer -->
      <div class="flex items-center justify-center gap-3 mt-3 text-xs tabular-nums">
        <label :for="inputId" class="label">stake</label>
        <span
          class="inline-flex items-center border border-gray-300 rounded bg-white px-2 py-1"
          ><span class="text-brandGrey mr-1">£</span
          ><input
            :id="inputId"
            type="number"
            min="0"
            step="1"
            inputmode="decimal"
            class="w-14 outline-none bg-transparent"
            :value="stake"
            aria-label="Stake in pounds"
            v-on:change="$emit('stake', $event.target.value)"
        /></span>
      </div>
      <p v-if="(totals && totals.returns) || pricing" class="mt-3 text-xs text-center tabular-nums">
        <span v-if="totals && totals.returns">
          returns <b>{{ money(totals.returns) }}</b> at
          <button type="button" class="term" v-on:click="toggle('best')">best odds</button>
          <template v-if="!locked && totals.returns_fair">
            · {{ money(totals.returns_fair) }} at
            <button type="button" class="term" v-on:click="toggle('fair')">fair odds</button>.</template
          >
        </span>
        <span v-else-if="pricing" class="text-brandGrey">pricing…</span>
      </p>
      <p v-if="totals && totals.correlated && !locked" class="text-xxs text-brandGrey text-center mt-3">
        Legs in the same match are priced together.
      </p>
    </div>
    <!-- the pitch: what members see that this user can't, then one clear button -->
    <div
      v-if="locked"
      class="absolute inset-0 rounded-xl bg-white/80 flex flex-col items-center justify-center gap-1.5 px-4 text-center"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" class="lock-icon text-blue-700" aria-hidden="true">
        <path
          fill="currentColor"
          d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7-2a2 2 0 0 1 4 0v2h-4z"
        />
      </svg>
      <p class="text-sm font-semibold text-gray-800">Is this slip worth it?</p>
      <p class="text-xxs text-gray-600 leading-snug max-w-[16rem]">
        Members see our chance for every leg, the fair odds and where the value is.
      </p>
      <router-link
        to="/signup/"
        class="mt-1 rounded-full bg-blue-700 text-white uppercase text-xs font-bold px-4 py-2"
        v-on:click.native="upgradeClick"
        >Unlock our numbers</router-link
      >
    </div>
  </div>
</template>

<script>
import { formatOdds, formatEdge, formatMoney } from "../../odds";
import { trackFeature } from "../../analytics";

let uid = 0;

export default {
  name: "SlipTotals",
  props: {
    totals: { type: Object, default: null },
    stake: { type: Number, default: 10 },
    locked: { type: Boolean, default: false },
    pricing: { type: Boolean, default: false },
  },
  data() {
    uid += 1;
    return {
      inputId: `slip-stake-${uid}`,
      explain: null, // "fair" | "best": the term whose explanation is open
      explanations: {
        fair: "Fair odds are the odds we think are fair, based on our AI predictions.",
        best: "Best odds are the highest price on offer from the bookmakers.",
      },
    };
  },
  computed: {
    // long fractions (e.g. 2399/100) shrink all three together so they never run into each other
    figureSize() {
      const longest = Math.max(
        ...[this.format(this.totals && this.totals.fair_odds), this.format(this.totals && this.totals.odds)].map(
          (t) => t.length,
        ),
      );
      if (longest > 9) return "figures-xs";
      if (longest > 7) return "figures-sm";
      return null;
    },
    edgeText() {
      return formatEdge(this.totals && this.totals.edge);
    },
    // the card's colours: value green, loss red, otherwise the same grey as the other figures
    edgeColour() {
      const edge = this.totals && this.totals.edge;
      const shown = edge === null || edge === undefined ? 0 : Math.round(edge);
      if (shown > 0) return "#22c55e";
      if (shown < 0) return "#FF6969";
      return null;
    },
  },
  methods: {
    toggle(term) {
      this.explain = this.explain === term ? null : term;
      if (this.explain) trackFeature("betslip_explain", "betslip", { term });
    },
    format(decimal) {
      return formatOdds(decimal, this.$store.getters.preferences.odds_format);
    },
    money: formatMoney,
    upgradeClick() {
      trackFeature("betslip_upgrade_click", "betslip", { from: "totals", legs: (this.totals && this.totals.priced_legs) || 0 });
    },
  },
};
</script>

<style scoped>
/* a term you can tap for its meaning */
.term {
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  cursor: help;
}
/* the match card's probability figures: text-lg, regular, grey */
.figure {
  display: block;
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
}
.figures-sm .figure {
  font-size: 1rem;
}
.figures-xs .figure {
  font-size: 0.875rem;
}
.lock-icon {
  color: #414141;
}
</style>
